/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useFetcher } from 'react-router';
import { resourceRoutes } from '@/route-paths';
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
import type { SelectedStoreInfo } from '@/extensions/store-locator/stores/store-locator-store';
import type {
    loader as searchStoresLoader,
    SearchStoresResult,
} from '@/extensions/store-locator/routes/resource.stores';

export type { SearchStoresResult };

/**
 * Hook to fetch and manage store locator list.
 * Triggers searchStores API when shouldSearch is true.
 *
 * @returns Read-only view of state and actions, including pagination helper
 *
 * @example
 * const { storesPaginated, setPage } = useStoreLocatorList();
 * // render list and call setPage((p)=>p+1) for Load More
 */
export function useStoreLocatorList() {
    const mode = useStoreLocator((s) => s.mode);
    const searchParams = useStoreLocator((s) => s.searchParams);
    const deviceCoordinates = useStoreLocator((s) => s.deviceCoordinates);
    const config = useStoreLocator((s) => s.config);
    const selectedStoreInfo = useStoreLocator((s) => s.selectedStoreInfo);
    const setSelectedStoreInfoRaw = useStoreLocator((s) => s.setSelectedStoreInfo);
    const geoError = useStoreLocator((s) => s.geoError);
    const shouldSearch = useStoreLocator((s) => s.shouldSearch);
    const setShouldSearch = useStoreLocator((s) => s.setShouldSearch);

    const fetcher = useFetcher<typeof searchStoresLoader>();
    const storeFetcher = useFetcher();
    const [page, setPage] = useState<number>(1);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    /*
     * Is an INTENTIONAL search in flight? State, not a ref, and cleared in an
     * effect rather than during render. Both of those were bugs and together they
     * are the whole of the owner's "i get no results originally then eventually it
     * flashes to denver".
     *
     * WHAT THE REF DID. It was set true in the dispatch effect, then a bare
     * `if (fetcher.state === 'idle' && isSearchingRef.current) ref = false` ran in
     * the RENDER BODY. `fetcher.load()` does not move the fetcher off `idle`
     * synchronously, so the very next render still saw `idle` and cleared the flag
     * BEFORE the request started. By the time the state became `loading` the flag
     * was already false, so `isLoading` never became true and THE SKELETON NEVER
     * RENDERED AT ALL — the list fell through to its terminal "no results" copy for
     * the entire duration of the fetch, then swapped to the stores. Nothing about
     * that reads as loading; it reads as a confident wrong answer followed by a
     * flash.
     *
     * A ref also cannot drive this even when cleared correctly: mutating one does
     * not re-render, so the transition it gates can be missed entirely.
     *
     * WHY IT STILL HAS TO BE A FLAG AND NOT JUST `fetcher.state !== 'idle'`: this
     * fetcher is also re-run by route revalidation, and a skeleton on every
     * revalidation would flash the list for something the shopper did not ask for.
     * The flag is what separates "the shopper searched" from "the router
     * revalidated" — which is the same distinction `resource.stores` protects on
     * the server between "no stores near here" and "the search did not happen".
     */
    const [isSearching, setIsSearching] = useState<boolean>(false);

    useEffect(() => {
        if (!shouldSearch) return;

        const params = new URLSearchParams();
        params.set('mode', mode);
        params.set('maxDistance', String(config.radius));
        params.set('distanceUnit', config.radiusUnit);
        params.set('limit', String(config.limit));
        let canFetch = false;

        if (mode === 'input') {
            const hasCountry = Boolean(searchParams?.countryCode);
            const hasPostal = Boolean(searchParams?.postalCode);
            if (hasCountry && hasPostal && searchParams) {
                params.set('countryCode', searchParams.countryCode);
                params.set('postalCode', searchParams.postalCode);
                canFetch = true;
            }
        } else if (mode === 'device') {
            const hasLat = typeof deviceCoordinates.latitude === 'number';
            const hasLng = typeof deviceCoordinates.longitude === 'number';
            if (hasLat && hasLng) {
                params.set('latitude', String(deviceCoordinates.latitude));
                params.set('longitude', String(deviceCoordinates.longitude));
                canFetch = true;
            }
        }

        if (canFetch) {
            setHasSearched(true);
            // Set BEFORE the load so the very next render already reports loading.
            // `fetcher.load()` leaves the state at `idle` for at least one render,
            // and that render is the one that used to paint "no results".
            setIsSearching(true);
            void fetcher.load(`${resourceRoutes.stores}?${params.toString()}`);
            setShouldSearch(false);
        }
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shouldSearch,
        mode,
        searchParams?.countryCode,
        searchParams?.postalCode,
        deviceCoordinates.latitude,
        deviceCoordinates.longitude,
    ]);

    /*
     * Clear it when the fetcher has SETTLED WITH A RESULT — both halves matter.
     * `state === 'idle'` alone is true in the gap before the request starts, which
     * is the bug this replaced. Requiring `fetcher.data` means the flag survives
     * that gap and clears only once there is something to render, including when
     * that something is an empty list or a failure: both are real answers and both
     * must leave the loading state.
     *
     * In an effect, not the render body — clearing it during render is a side
     * effect in a pure function and was reading a value that had not updated yet.
     */
    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data !== undefined) {
            setIsSearching(false);
        }
    }, [fetcher.state, fetcher.data]);

    const stores = useMemo(() => fetcher.data?.stores?.data ?? [], [fetcher.data]);
    const hasError = useMemo(() => fetcher.data?.success === false, [fetcher.data]);
    const showCount = page * 10;
    const storesPaginated = stores.slice(0, showCount);

    // Only for intentional searches, never revalidation — and true across the whole
    // window, including the render where the fetcher has not left `idle` yet.
    const isLoading = isSearching;

    // Wrapper function that updates store selection via server action.
    // Optimistically updates client state, then submits to server action which
    // sets the cookie via middleware Set-Cookie. React Router auto-revalidates after action.
    const setSelectedStoreInfo = useCallback(
        (info: SelectedStoreInfo | null) => {
            // Optimistic client state update
            setSelectedStoreInfoRaw(info);

            // Submit to server action for cookie persistence
            const formData = new FormData();
            formData.set('storeInfo', info ? JSON.stringify(info) : '');
            void storeFetcher.submit(formData, { method: 'POST', action: resourceRoutes.setSelectedStore });
        },
        [setSelectedStoreInfoRaw, storeFetcher]
    );

    return {
        // store state
        mode,
        searchParams,
        config,
        selectedStoreInfo,
        setSelectedStoreInfo,
        geoError,
        // fetch state
        hasSearched,
        hasError,
        isLoading,
        stores,
        storesPaginated,
        setPage,
    } as const;
}
